/* global globalThis */
import React, { useState, useRef, useEffect } from "react";
import logo from "../../assets/chatbot.png";
import { Menu } from "lucide-react";
import axiosClient from "../../api-client/AxiosClient";
import appConfig from "../../config/appConfig";
import { toast } from "react-toastify";
import "./ChatHistoryMenu.css";
import { validateCorporationData } from "../../utils/dataValidation";
import ChatSidebar from "../ui/ChatSidebar";
import ChatMessages from "../ui/ChatMessages";
import ChatInput from "../ui/ChatInput";
import DeleteConfirmModal from "../ui/DeleteConfirmModal";

const BASE_URL = process.env.REACT_APP_AGENT_API_BASE_URL;
const API_CODE = process.env.REACT_APP_API_CODE;


// Helper functions extracted to reduce complexity
const parseMessageContent = (content) => {
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const extractArrayData = (data, listKeys) => {
  for (const key of listKeys) {
    if (data[key] && Array.isArray(data[key]) && data[key].length > 0) {
      return data[key];
    }
  }

  const allKeys = Object.keys(data);
  for (const key of allKeys) {
    if (Array.isArray(data[key]) && data[key].length > 0) {
      return data[key];
    }
  }

  return null;
};

// Add this helper component before FalconAIChatbot
// ─── Hierarchy Preview Modal ───────────────────────────────────────────────
// Paste this block BEFORE the FalconAIChatbot component definition.
// It replaces the old PreviewTabs component entirely.

/** Build nested structure: { [targetTable]: { [sourceTable]: [mappingRows] } } */
const buildHierarchy = (mappingRows) => {
  const hierarchy = {};
  mappingRows.forEach((row) => {
    const target =
      row.sap_table_name ||
      row.SAP_TABLE_NAME ||
      row.TargetTable ||
      "Unknown Target";
    const source =
      row.ax_table_name ||
      row.AX_TABLE_NAME ||
      row.SourceTable ||
      "Unknown Source";
    if (!hierarchy[target]) hierarchy[target] = {};
    if (!hierarchy[target][source]) hierarchy[target][source] = [];
    hierarchy[target][source].push(row);
  });
  return hierarchy;
};

/** Collect highlighted field names per source table */
const buildHighlightMap = (mappingRows) => {
  const map = {}; // { [ax_table_name]: Set<ax_field_name_UPPERCASE> }
  mappingRows.forEach((row) => {
    const src = row.ax_table_name || row.AX_TABLE_NAME || row.SourceTable;
    const field = row.ax_field_name || row.AX_FIELD_NAME || row.SourceField;
    if (src && field) {
      if (!map[src]) map[src] = new Set();
      map[src].add(field.toUpperCase());
    }
  });
  return map;
};

const HierarchyPreviewModal = ({
  open,
  onClose,
  previewData, // { [sourceTable]: [rows] }  — from API
  previewLoading,
  mappingRows, // raw mapping array for hierarchy + highlight logic
}) => {
  const [expandedTargets, setExpandedTargets] = React.useState({});
  const [expandedSources, setExpandedSources] = React.useState({});
  const [selectedSource, setSelectedSource] = React.useState(null); // { target, source }

  if (!open) return null;

  const hierarchy = buildHierarchy(mappingRows || []);
  const highlightMap = buildHighlightMap(mappingRows || []);
  const targetTables = Object.keys(hierarchy);

  const toggleTarget = (target) =>
    setExpandedTargets((p) => ({ ...p, [target]: !p[target] }));

  const toggleSource = (target, source) => {
    const key = `${target}::${source}`;
    setExpandedSources((p) => ({ ...p, [key]: !p[key] }));
    setSelectedSource({ target, source });
  };

  const activeRows =
    selectedSource && previewData[selectedSource.source]
      ? previewData[selectedSource.source]
      : null;

  const highlightedFields = selectedSource
    ? highlightMap[selectedSource.source] || new Set()
    : new Set();

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.15)",
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "92vw", maxWidth: 1300, height: "88vh" }}
      >
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            {/* Icon badge */}
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <path d="M14 17h7M17.5 14v7" />
              </svg>
            </div>
            <div>
              <h2 className="text-white font-bold text-base leading-tight tracking-tight">
                Mapping Hierarchy Preview
              </h2>
              <p className="text-blue-200 text-xs mt-0.5 font-medium">
                {targetTables.length} target table
                {targetTables.length !== 1 ? "s" : ""} · Select a source table
                to preview data
              </p>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            title="Close"
            className="flex items-center justify-center rounded-lg transition-all"
            style={{
              width: 34,
              height: 34,
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.25)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.12)")
            }
          >
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              viewBox="0 0 24 24"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Body: sidebar + main panel ── */}
        <div className="flex flex-1 overflow-hidden">
          {/* ── LEFT SIDEBAR: Hierarchy tree ── */}
          <div
            className="flex-shrink-0 border-r border-gray-100 overflow-y-auto bg-gray-50"
            style={{ width: 290 }}
          >
            <div className="p-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-3">
                Table Hierarchy
              </p>

              {previewLoading ? (
                <div className="flex items-center justify-center py-10 text-gray-400 text-sm gap-2">
                  <span className="animate-spin">⏳</span> Loading…
                </div>
              ) : targetTables.length === 0 ? (
                <p className="text-xs text-gray-400 px-2">
                  No hierarchy available.
                </p>
              ) : (
                targetTables.map((target) => {
                  const sourceTables = Object.keys(hierarchy[target]);
                  const isTargetOpen = !!expandedTargets[target];

                  return (
                    <div key={target} className="mb-1">
                      {/* ── Target row ── */}
                      <button
                        onClick={() => toggleTarget(target)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-all hover:bg-blue-50 group"
                      >
                        <span
                          className={`text-gray-400 text-xs transition-transform duration-200 ${
                            isTargetOpen ? "rotate-90" : ""
                          }`}
                          style={{ display: "inline-block" }}
                        >
                          ▶
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-semibold text-gray-800 truncate block">
                            {target}
                          </span>
                          <span className="text-xs text-gray-400">
                            {sourceTables.length} source table
                            {sourceTables.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-bold flex-shrink-0">
                          T
                        </span>
                      </button>

                      {/* ── Source tables ── */}
                      {isTargetOpen && (
                        <div className="ml-4 border-l-2 border-blue-100 pl-2 mt-0.5 mb-1">
                          {sourceTables.map((source) => {
                            const srcKey = `${target}::${source}`;
                            const isSrcOpen = !!expandedSources[srcKey];
                            const isActive =
                              selectedSource?.target === target &&
                              selectedSource?.source === source;
                            const mappedCols = hierarchy[target][source];

                            return (
                              <div key={source} className="mb-0.5">
                                <button
                                  onClick={() => toggleSource(target, source)}
                                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-all ${
                                    isActive
                                      ? "bg-blue-600 text-white shadow-sm"
                                      : "hover:bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  <span
                                    className={`text-xs transition-transform duration-200 ${
                                      isSrcOpen ? "rotate-90" : ""
                                    } ${isActive ? "text-white" : "text-gray-400"}`}
                                    style={{ display: "inline-block" }}
                                  >
                                    ▶
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-xs font-medium truncate block">
                                      {source}
                                    </span>
                                    <span
                                      className={`text-xs ${
                                        isActive
                                          ? "text-blue-200"
                                          : "text-gray-400"
                                      }`}
                                    >
                                      {mappedCols.length} mapping
                                      {mappedCols.length !== 1 ? "s" : ""}
                                    </span>
                                  </div>
                                  <span
                                    className={`text-xs rounded-full px-2 py-0.5 font-bold flex-shrink-0 ${
                                      isActive
                                        ? "bg-blue-500 text-white"
                                        : "bg-green-100 text-green-700"
                                    }`}
                                  >
                                    S
                                  </span>
                                </button>

                                {/* ── Mapped field list ── */}
                                {isSrcOpen && (
                                  <div className="ml-3 mt-0.5 mb-1 space-y-0.5">
                                    {[
                                      ...new Set(
                                        mappedCols.map(
                                          (row) =>
                                            row.ax_field_name ||
                                            row.AX_FIELD_NAME ||
                                            "—",
                                        ),
                                      ),
                                    ].map((fieldName, i) => (
                                      <div
                                        key={i}
                                        className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-gray-500 hover:bg-yellow-50 hover:text-yellow-800 transition-colors"
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0" />
                                        <span
                                          className="font-mono truncate"
                                          title={fieldName}
                                        >
                                          {fieldName}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── RIGHT PANEL: Table data ── */}
          <div className="flex-1 overflow-hidden flex flex-col bg-white">
            {!selectedSource ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3 px-6 text-center">
                <svg
                  className="w-14 h-14 text-gray-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Select a source table
                  </p>
                  <p className="text-xs text-gray-400 mt-1 max-w-xs">
                    Expand a target table from the left panel, then click a
                    source table to preview its data with highlighted mapped
                    columns.
                  </p>
                </div>
              </div>
            ) : previewLoading ? (
              <div className="flex items-center justify-center h-full gap-2 text-gray-400">
                <span className="animate-spin text-blue-500">⏳</span>
                <span className="text-sm">Loading preview data…</span>
              </div>
            ) : (
              <>
                {/* Sub-header breadcrumb */}
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0 flex-wrap">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-md font-semibold">
                      {selectedSource.target}
                    </span>
                    <svg
                      className="w-3 h-3 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-md font-semibold">
                      {selectedSource.source}
                    </span>
                  </div>
                  <div className="ml-auto flex items-center gap-3">
                    {activeRows && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                        {activeRows.length} rows
                      </span>
                    )}
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-400 inline-block" />
                      <span className="text-xs text-gray-500">
                        = mapped field
                      </span>
                    </div>
                  </div>
                </div>

                {/* Data table */}
                <div className="flex-1 overflow-hidden p-4 relative flex flex-col">
                  {!activeRows || activeRows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
                      <svg
                        className="w-8 h-8 text-gray-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm">
                        No data available for{" "}
                        <strong>{selectedSource.source}</strong>
                      </span>
                    </div>
                  ) : (
                    <div className="overflow-auto flex-1 h-full">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr>
                            {Object.keys(activeRows[0]).map((col) => {
                              const isHighlighted = highlightedFields.has(
                                col.toUpperCase(),
                              );
                              return (
                                <th
                                  key={col}
                                  className={`sticky top-0 z-20 text-left px-3 py-2.5 border-b font-semibold whitespace-nowrap shadow-sm ${
                                    isHighlighted
                                      ? "bg-yellow-50 text-yellow-800 border-yellow-300"
                                      : "bg-gray-50 text-gray-600 border-gray-200"
                                  }`}
                                >
                                  <div className="flex items-center gap-1">
                                    {isHighlighted && (
                                      <span className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
                                    )}
                                    {col}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {activeRows.map((row, ri) => (
                            <tr
                              key={ri}
                              className={
                                ri % 2 === 0 ? "bg-white" : "bg-gray-50/40"
                              }
                            >
                              {Object.entries(row).map(([col, val]) => {
                                const isHighlighted = highlightedFields.has(
                                  col.toUpperCase(),
                                );
                                return (
                                  <td
                                    key={col}
                                    className={`px-3 py-2 border-b whitespace-nowrap ${
                                      isHighlighted
                                        ? "bg-yellow-50 text-yellow-900 font-semibold border-yellow-200"
                                        : "text-gray-700 border-gray-100"
                                    }`}
                                  >
                                    {val !== null && val !== undefined ? (
                                      val
                                    ) : (
                                      <span className="text-gray-300">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <span className="text-xs text-gray-400">
            {selectedSource
              ? `Previewing: ${selectedSource.source} — ${
                  activeRows?.length ?? 0
                } row(s) · Yellow columns = mapped fields from your selection`
              : "Select a source table from the hierarchy to view its data"}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg text-xs font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const processAssistantMessage = (parsedContent) => {
  let processedData = null;
  let processedText = parsedContent.content;
  let hasDownloadableData = false;

  if (parsedContent.processing_status === "error") {
    processedText =
      parsedContent.error_msg ||
      parsedContent.detail ||
      parsedContent.message ||
      "An error occurred";
    return { processedData, processedText, hasDownloadableData };
  }

  if (parsedContent.error && typeof parsedContent.error === "string") {
    processedText = parsedContent.error;
    return { processedData, processedText, hasDownloadableData };
  }

  // NEW CODE: Handle answer field - display as plain text
  if (parsedContent.answer) {
    // Remove markdown headers (#) and format text
    processedText = parsedContent.answer
      .replaceAll(/^#+\s*/gm, "") // Remove # headers
      .replaceAll("**", "") // Remove bold markdown
      .trim();
    return { processedData: null, processedText, hasDownloadableData: false };
  }

  if (
    parsedContent.response?.detail &&
    typeof parsedContent.response.detail === "string" &&
    !Array.isArray(parsedContent.response.detail)
  ) {
    processedText = parsedContent.response.detail;
    return { processedData, processedText, hasDownloadableData };
  }

  if (
    Array.isArray(parsedContent.response) &&
    parsedContent.response.length > 0
  ) {
    processedData = parsedContent.response;
    processedText = "Here are the results:";
    hasDownloadableData = true;
    return { processedData, processedText, hasDownloadableData };
  }

  if (
    parsedContent.response &&
    typeof parsedContent.response === "object" &&
    !Array.isArray(parsedContent.response)
  ) {
    if (parsedContent.response.ax_tables && parsedContent.response.sap_tables) {
      processedData = {
        ax_tables: parsedContent.response.ax_tables,
        sap_tables: parsedContent.response.sap_tables,
      };
      processedText = parsedContent.response.message || "Here are the results:";
      hasDownloadableData = true;
      return { processedData, processedText, hasDownloadableData };
    }

    const listKeys = [
      "ax_tables",
      "sap_tables",
      "tables",
      "columns",
      "results",
      "data",
      "mappings",
      "source_mappings",
      "target_mappings",
    ];
    const foundListData = extractArrayData(parsedContent.response, listKeys);

    if (foundListData) {
      processedData = foundListData;
      processedText = parsedContent.response.message || "Here are the results:";
      hasDownloadableData = true;
      return { processedData, processedText, hasDownloadableData };
    }

    processedText =
      parsedContent.response.message ||
      parsedContent.response.detail ||
      JSON.stringify(parsedContent.response);
    return { processedData, processedText, hasDownloadableData };
  }

  if (Array.isArray(parsedContent) && parsedContent.length > 0) {
    processedData = parsedContent;
    processedText = "Here are the results:";
    hasDownloadableData = true;
    return { processedData, processedText, hasDownloadableData };
  }

  const arrayKeys = Object.keys(parsedContent).filter(
    (key) => Array.isArray(parsedContent[key]) && parsedContent[key].length > 0,
  );

  if (arrayKeys.length > 0) {
    processedData = parsedContent[arrayKeys[0]];
    processedText =
      parsedContent.message || parsedContent.detail || "Here are the results:";
    hasDownloadableData = true;
    return { processedData, processedText, hasDownloadableData };
  }

  processedText =
    parsedContent.message ||
    parsedContent.detail ||
    parsedContent.error_msg ||
    JSON.stringify(parsedContent);
  return { processedData, processedText, hasDownloadableData };
};

const createUserMessage = (msg) => ({
  id: msg.message_id ? `user-${msg.message_id}` : `user-${Date.now()}`,
  sender: "user",
  text: msg.content,
  timestamp: new Date(Number.parseInt(msg.timestamp)),
  hasDownload: false,
});
////////////
const createAIMessage = (
  msg,
  processedText,
  hasDownloadableData,
  userQuery,
  processedData,
  parsedContent,
) => {
  // Clean markdown from text before creating message
  const cleanedText =
    typeof processedText === "string"
      ? processedText
          .replaceAll(/^#+\s*/gm, "") // Remove # headers
          .replaceAll("**", "") // Remove bold markdown
          .trim()
      : processedText;

  return {
    id: msg.message_id || Date.now(),
    sender: "ai",
    text: cleanedText,
    timestamp: new Date(Number.parseInt(msg.timestamp)),
    hasDownload: hasDownloadableData,
    originalQuery: userQuery || msg.original_query,
    data: processedData,
    mappingFileId: parsedContent?.mapping_file_id || null,
  };
};

const REJECT_REASONS = [
  { value: "missing_reference", label: "Missing Reference" },
  { value: "incorrect_mapping", label: "Incorrect Mapping" },
  { value: "duplicate_entry", label: "Duplicate Entry" },
  { value: "out_of_scope", label: "Out of Scope" },
  { value: "other", label: "Other" },
];

const SEVERITY_LEVELS = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

const RejectConfirmModal = ({
  rejectModal,
  setRejectModal,
  onConfirm,
  isRejecting,
}) => {
  if (!rejectModal.open) return null;

  const { selectedData, reason, severity, comment } = rejectModal;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "680px", maxWidth: "95vw", maxHeight: "88vh" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{
            background: "linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-white font-bold text-base leading-tight">
                Reject Mapping(s)
              </h2>
              <p className="text-red-200 text-xs mt-0.5">
                {selectedData.length} mapping
                {selectedData.length !== 1 ? "s" : ""} selected for rejection
              </p>
            </div>
          </div>
          <button
            onClick={() => setRejectModal((p) => ({ ...p, open: false }))}
            className="flex items-center justify-center rounded-lg transition-all"
            style={{
              width: 34,
              height: 34,
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.25)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.12)")
            }
          >
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              viewBox="0 0 24 24"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Selected mappings preview table */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Mappings Being Rejected
            </p>
            <div className="border border-red-100 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-red-700 border-b border-red-100">
                      #
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-red-700 border-b border-red-100">
                      SAP Table
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-red-700 border-b border-red-100">
                      SAP Column
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-red-700 border-b border-red-100">
                      AX Table
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-red-700 border-b border-red-100">
                      AX Field
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedData.map((row, i) => (
                    <tr
                      key={i}
                      className={i % 2 === 0 ? "bg-white" : "bg-red-50/30"}
                    >
                      <td className="px-3 py-2 text-gray-500 border-b border-red-50">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800 border-b border-red-50">
                        {row.sap_table_name || row.SAPTableName || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-700 border-b border-red-50">
                        {row.sap_column_name || row.SAPColumnName || "—"}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800 border-b border-red-50">
                        {row.ax_table_name || row.AXTableName || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-700 border-b border-red-50">
                        {row.ax_field_name || row.AXColumnName || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Reason + Severity row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={reason}
                onChange={(e) =>
                  setRejectModal((p) => ({ ...p, reason: e.target.value }))
                }
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 transition-all"
              >
                {REJECT_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) =>
                  setRejectModal((p) => ({ ...p, severity: e.target.value }))
                }
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 transition-all"
              >
                {SEVERITY_LEVELS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Additional Comments{" "}
              <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) =>
                setRejectModal((p) => ({ ...p, comment: e.target.value }))
              }
              placeholder="Describe why these mappings are being rejected..."
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 transition-all"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <span className="text-xs text-gray-400">
            This action will submit negative feedback for the selected mappings.
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setRejectModal((p) => ({ ...p, open: false }))}
              className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isRejecting}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white rounded-lg transition-colors ${
                isRejecting
                  ? "bg-red-300 cursor-not-allowed"
                  : "bg-red-500 hover:bg-red-600"
              }`}
            >
              {isRejecting ? (
                <>
                  <span className="animate-spin">⏳</span> Submitting...
                </>
              ) : (
                <>
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>{" "}
                  Confirm Rejection
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FalconAIChatbot = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(1);
  const [message, setMessage] = useState("");
  const [selectedRows, setSelectedRows] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [setMapperFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectModal, setRejectModal] = useState({
    open: false,
    messageId: null,
    selectedData: [],
    mappingFileId: null,
    reason: "missing_reference",
    severity: "Low",
    comment: "",
  });
  const [regeneratingMessageId, setRegeneratingMessageId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({}); // now an object keyed by table name
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTableName, setPreviewTableName] = useState([]); // now an array
  const [previewMappingRows, setPreviewMappingRows] = useState([]);

  const [selectedCorporation, setSelectedCorporation] = useState({
    label: "",
    value: "",
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [renamingChatId, setRenamingChatId] = useState(null);
  const [organizationConfigId, setOrganizationConfigId] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chats]);

  useEffect(() => {
    const fetchMapperFiles = async () => {
      try {
        const response = await axiosClient.get("/api/Mapper/GetMapperFiles");
        setMapperFiles(response.data);
      } catch (error) {
        console.error("Error fetching mapper files:", error);
      }
    };

    fetchMapperFiles();
  }, [setMapperFiles]);

  const getCurrentUserEmail = () => {
    try {
      const accountKey = JSON.parse(
        sessionStorage.getItem("msal.account.keys"),
      )?.[0];
      if (accountKey) {
        const accountData = JSON.parse(sessionStorage.getItem(accountKey));
        return accountData?.username || null;
      }
      return null;
    } catch (error) {
      console.error("Error getting current user email:", error);
      return null;
    }
  };

  // AFTER
  const handlePreview = async (tableNames, mappingFileId, mappingRows = []) => {
    if (!tableNames || tableNames.length === 0) {
      toast.error("No table names available for preview");
      return;
    }

    // ✅ Allow preview even without mappingFileId — just warn
    if (!mappingFileId) {
      toast.warning("Mapping file ID is missing — preview may be limited");
    }

    const tableNamesArray = Array.isArray(tableNames)
      ? tableNames
      : [tableNames];

    setPreviewTableName(tableNamesArray);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData({});
    setActivePreviewTab("");
    setPreviewMappingRows(mappingRows); // ← ADD THIS LINE

    try {
      const response = await axiosClient.post(
        "/api/Mapper/GetAvailbleTableRow",
        {
          userMappingFileId: mappingFileId ?? "", // ✅ send empty string instead of crashing
          cacheRefreshRequired: true,
          tableNames: tableNamesArray,
        },
      );

      const data = response.data || {};
      setPreviewData(data);
      const firstKey = Object.keys(data)[0];
      if (firstKey) setActivePreviewTab(firstKey);
    } catch (error) {
      console.error("Preview fetch error:", error);
      toast.error("Failed to load preview data");
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchCurrentUserId = async () => {
    try {
      const currentUserEmail = getCurrentUserEmail();

      if (!currentUserEmail) {
        return null;
      }

      const response = await axiosClient.get("/api/User/all");

      if (!response?.data) {
        return null;
      }

      const allUsers = Array.isArray(response.data) ? response.data : [];
      const normalizedCurrentEmail = currentUserEmail.toLowerCase().trim();

      let matchedUser = allUsers.find(
        (user) =>
          user.email &&
          user.email.toLowerCase().trim() === normalizedCurrentEmail,
      );

      if (!matchedUser) {
        matchedUser = allUsers.find((user) => {
          if (!user.email) return false;
          const normalizedUserEmail = user.email.toLowerCase().trim();
          return (
            normalizedUserEmail.includes(normalizedCurrentEmail) ||
            normalizedCurrentEmail.includes(normalizedUserEmail)
          );
        });
      }

      if (matchedUser) {
        const userId = matchedUser.userId || matchedUser.id || null;
        return userId;
      }

      return null;
    } catch (error) {
      console.error("Error fetching current user ID:", error);
      return null;
    }
  };

  const getWelcomeMessage = (corporationValue) => {
    const template = localStorage.getItem("template");
    const mappingText = template || "SAP S/4HANA ➝ AX mapping queries";

    return `👋 Hello!  
I am your Mapping Assistant. I can help you with:  
- ${mappingText} mapping queries
- Download results as CSV  
Let's get started! 🚀`;
  };

  useEffect(() => {
    const storedCorporation = localStorage.getItem("selectedCorporation");
    if (storedCorporation) {
      const parsedCorporation = JSON.parse(storedCorporation);
      setSelectedCorporation(parsedCorporation);
    }

    const storedOrgConfigId = localStorage.getItem("organization_config_id");
    if (storedOrgConfigId) {
      setOrganizationConfigId(storedOrgConfigId);
    }
  }, []);

  useEffect(() => {
    const handleOrgConfigChange = () => {
      const storedOrgConfigId = localStorage.getItem("organization_config_id");
      if (storedOrgConfigId && storedOrgConfigId !== organizationConfigId) {
        setOrganizationConfigId(storedOrgConfigId);
        // Clear existing chats and reset
        setChats([]);
        setActiveChat(null);
        setSelectedRows({});
      }
    };

    // Listen for storage changes
    globalThis.addEventListener("storage", handleOrgConfigChange);

    // Also listen for custom event
    globalThis.addEventListener("customStorageChange", handleOrgConfigChange);

    return () => {
      globalThis.removeEventListener("storage", handleOrgConfigChange);
      globalThis.removeEventListener(
        "customStorageChange",
        handleOrgConfigChange,
      );
    };
  }, [organizationConfigId]);

  const handleCorporationChange = (validatedCorp) => {
    if (validatedCorp.value !== selectedCorporation.value) {
      setSelectedCorporation(validatedCorp);
      const storedOrgConfigId = localStorage.getItem("organization_config_id");
      if (storedOrgConfigId) {
        setOrganizationConfigId(storedOrgConfigId);
      }
      setChats([]);
      setActiveChat(null);
      setSelectedRows({});

      // ADD THIS: Create a welcome chat immediately
      const welcomeChat = {
        id: Date.now(),
        title: "New Chat",
        messages: [
          {
            id: Date.now(),
            sender: "ai",
            text: getWelcomeMessage(validatedCorp.value),
            timestamp: new Date(),
            hasDownload: false,
          },
        ],
        isLoaded: true,
      };

      setChats([welcomeChat]);
      setActiveChat(welcomeChat.id);
    }
  };

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "selectedCorporation") {
        try {
          if (e.newValue === null) {
            setSelectedCorporation({ label: "", value: "" });
            setChats([]);
            setActiveChat(null);
            return;
          }

          const parsedCorporation = JSON.parse(e.newValue);
          const validatedCorp = validateCorporationData(parsedCorporation);
          handleCorporationChange(validatedCorp);
        } catch (error) {
          console.error("Storage change error:", error);
        }
      }
    };

    const handleCustomStorageChange = (e) => {
      if (e.detail?.key === "selectedCorporation") {
        try {
          if (e.detail.newValue === null || e.detail.newValue === undefined) {
            setSelectedCorporation({ label: "", value: "" });
            setChats([]);
            setActiveChat(null);
            return;
          }

          let corporationData = e.detail.newValue;

          if (typeof corporationData === "string") {
            try {
              corporationData = JSON.parse(corporationData);
            } catch (parseError) {
              console.error("Error parsing corporation data:", parseError);
              return;
            }
          }

          const validatedCorp = validateCorporationData(corporationData);
          handleCorporationChange(validatedCorp);
        } catch (error) {
          console.error("Custom storage error:", error);
        }
      }
    };

    globalThis.addEventListener("storage", handleStorageChange);
    globalThis.addEventListener(
      "customStorageChange",
      handleCustomStorageChange,
    );

    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      globalThis.removeEventListener(
        "customStorageChange",
        handleCustomStorageChange,
      );
    };
  }, [selectedCorporation]);

  useEffect(() => {
    const loadUserId = async () => {
      const userId = await fetchCurrentUserId();
      setCurrentUserId(userId);
    };
    loadUserId();
  }, []);

  const fetchAllConversations = async () => {
    try {
      const response = await fetch(
      `${BASE_URL}/conversations?code=${API_CODE}&user_id=${currentUserId}&organization_config_id=${organizationConfigId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const conversationsArray = data.conversations || data;
      return { success: true, data: conversationsArray };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  };

  const fetchConversationDetails = async (threadId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/conversations/${threadId}?code=${API_CODE}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateConversation = async (threadId, title) => {
    try {
      const payload = {
        title: title,
        thread_id: threadId,
      };

      const response = await fetch(
        `${BASE_URL}/conversations?code=${API_CODE}`,
        {
          method: "PUT",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error("Error updating conversation:", error);
      return { success: false, error: error.message };
    }
  };

  const deleteConversation = async (threadId) => {
    try {
      const payload = {
        thread_id: threadId,
      };

      const response = await fetch(
        `${BASE_URL}/conversations/delete?code=${API_CODE}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const processMessages = (messages) => {
    const loadedMessages = [];

    messages.forEach((msg) => {
      if (msg.role === "user") {
        loadedMessages.push(createUserMessage(msg));
        return;
      }

      if (msg.role !== "assistant") return;

      let parsedContent = parseMessageContent(msg.content);
      let isPlainText = !parsedContent;
      let userQuery = null;

      if (isPlainText) {
        // Try to parse as JSON to extract error messages
        let cleanedContent = msg.content;

        try {
          const possibleJson = JSON.parse(msg.content);

          // Extract error message from JSON
          if (possibleJson.error) {
            cleanedContent = possibleJson.error;
          } else if (possibleJson.message) {
            cleanedContent = possibleJson.message;
          } else if (possibleJson.detail) {
            cleanedContent = possibleJson.detail;
          } else if (possibleJson.error_msg) {
            cleanedContent = possibleJson.error_msg;
          } else {
            // If it's JSON but doesn't have known error fields, stringify it
            cleanedContent = JSON.stringify(possibleJson);
          }
        } catch (e) {
          // Not JSON, use as is
          cleanedContent = msg.content;
        }

        // Clean markdown from the extracted text
        cleanedContent = cleanedContent
          .replaceAll(/^#+\s*/gm, "") // Remove # headers
          .replaceAll("**", "") // Remove bold markdown
          .trim();

        loadedMessages.push({
          id: msg.message_id || Date.now(),
          sender: "ai",
          text: cleanedContent,
          timestamp: new Date(Number.parseInt(msg.timestamp)),
          hasDownload: false,
        });
        return;
      }
      if (parsedContent.query) {
        userQuery = parsedContent.query;
      }

      if (
        parsedContent.response &&
        typeof parsedContent.response === "string"
      ) {
        try {
          parsedContent.response = JSON.parse(parsedContent.response);
        } catch (e) {
          console.error("Error parsing response:", e);
          // Keep as string if parsing fails
        }
      }

      const { processedData, processedText, hasDownloadableData } =
        processAssistantMessage(parsedContent);

      if (userQuery || msg.original_query) {
        loadedMessages.push({
          id: msg.message_id ? `user-${msg.message_id}` : `user-${Date.now()}`,
          sender: "user",
          text: userQuery || msg.original_query,
          timestamp: new Date(Number.parseInt(msg.timestamp)),
          hasDownload: false,
        });
      }

      loadedMessages.push(
        createAIMessage(
          msg,
          processedText,
          hasDownloadableData,
          userQuery,
          processedData,
          parsedContent,
        ),
      );
    });

    return loadedMessages;
  };

  const loadChatMessages = async (threadId) => {
    if (!threadId) {
      return [];
    }

    const existingChat = chats.find((chat) => chat.id === threadId);
    if (existingChat?.messages?.length > 0) {
      return existingChat.messages;
    }

    try {
      setLoadingMessages(true);

      const result = await fetchConversationDetails(threadId);

      if (result.success && result.data?.messages) {
        const loadedMessages = processMessages(result.data.messages);

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === threadId
              ? { ...chat, messages: loadedMessages, isLoaded: true }
              : chat,
          ),
        );

        setLoadingMessages(false);
        return loadedMessages;
      }

      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === threadId
            ? { ...chat, messages: [], isLoaded: true }
            : chat,
        ),
      );

      setLoadingMessages(false);
      return [];
    } catch (error) {
      console.error("Error loading chat messages:", error);
      toast.error("Failed to load chat messages");
      setLoadingMessages(false);
      return [];
    }
  };

  useEffect(() => {
    const loadConversations = async () => {
      if (!currentUserId || !selectedCorporation.value) {
        const welcomeChat = {
          id: Date.now(),
          title: "New Chat",
          messages: [
            {
              id: Date.now(),
              sender: "ai",
              text: getWelcomeMessage(selectedCorporation.value),
              timestamp: new Date(),
              hasDownload: false,
            },
          ],
          isLoaded: true,
        };
        setChats([welcomeChat]);
        setActiveChat(welcomeChat.id);
        return;
      }

      const result = await fetchAllConversations();

      if (result.success && result.data && result.data.length > 0) {
        const loadedChats = result.data.map((conv) => ({
          id: conv.thread_id,
          title: conv.title || "New Chat",
          messages: [],
          createdAt: conv.created_at,
          updatedAt: conv.updated_at,
          isLoaded: false,
        }));

        const newChatId = Date.now();
        const newChat = {
          id: newChatId,
          title: "New Chat",
          messages: [
            {
              id: newChatId + 1,
              sender: "ai",
              text: getWelcomeMessage(selectedCorporation.value),
              timestamp: new Date(),
              hasDownload: false,
            },
          ],
          isLoaded: true,
        };

        setChats([newChat, ...loadedChats]);
        setActiveChat(newChatId);
      } else {
        const welcomeChat = {
          id: Date.now(),
          title: "New Chat",
          messages: [
            {
              id: Date.now(),
              sender: "ai",
              text: getWelcomeMessage(selectedCorporation.value),
              timestamp: new Date(),
              hasDownload: false,
            },
          ],
          isLoaded: true,
        };
        setChats([welcomeChat]);
        setActiveChat(welcomeChat.id);
      }
    };

    loadConversations();
  }, [currentUserId, selectedCorporation.value, organizationConfigId]);

  useEffect(() => {
    const loadMessagesForActiveChat = async () => {
      if (!activeChat) return;
      const currentChatData = chats.find((chat) => chat.id === activeChat);
      if (currentChatData && !currentChatData.isLoaded) {
        await loadChatMessages(activeChat);
      }
    };

    loadMessagesForActiveChat();
  }, [activeChat]);

  const createNewChat = () => {
    const newChatId = Date.now();
    const newChat = {
      id: newChatId,
      title: "New Chat",
      messages: [
        {
          id: newChatId + 1,
          sender: "ai",
          text: getWelcomeMessage(selectedCorporation.value),
          timestamp: new Date(),
          hasDownload: false,
        },
      ],
      isLoaded: true,
    };
    setChats([newChat, ...chats]);
    setActiveChat(newChatId);
  };

  const toggleMenu = (chatId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === chatId ? null : chatId);
  };

  const handleRenameClick = (chatId, currentTitle, e) => {
    e.stopPropagation();
    setRenamingChatId(chatId);
    setRenameValue(currentTitle);
    setOpenMenuId(null);
  };

  const handleRenameSubmit = async (chatId) => {
    if (renameValue.trim()) {
      setChats(
        chats.map((chat) =>
          chat.id === chatId ? { ...chat, title: renameValue.trim() } : chat,
        ),
      );

      const result = await updateConversation(chatId, renameValue.trim());

      if (result.success) {
        toast.success("Chat renamed successfully!");
      } else {
        toast.error("Failed to rename chat on server");
        console.error("Rename failed:", result.error);
      }
    }
    setRenamingChatId(null);
    setRenameValue("");
  };

  const handleRenameCancel = () => {
    setRenamingChatId(null);
    setRenameValue("");
  };

  const handleDeleteClick = (chatId, e) => {
    e.stopPropagation();
    setDeleteConfirmId(chatId);
    setOpenMenuId(null);
  };

  const handleDeleteConfirm = async () => {
    if (chats.length === 1) {
      toast.warning("Cannot delete the last chat!");
      setDeleteConfirmId(null);
      return;
    }

    const result = await deleteConversation(deleteConfirmId);

    if (result.success) {
      const updatedChats = chats.filter((chat) => chat.id !== deleteConfirmId);
      setChats(updatedChats);

      if (activeChat === deleteConfirmId) {
        setActiveChat(updatedChats[0].id);
        loadChatMessages(updatedChats[0].id);
      }

      toast.success("Chat deleted successfully!");
    } else {
      toast.error("Failed to delete chat");
    }

    setDeleteConfirmId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleRowSelect = (messageId, rowIndex) => {
    const key = `${messageId}-${rowIndex}`;
    setSelectedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = (messageId, totalRows) => {
    const allSelected = Array.from({ length: totalRows }).every(
      (_, i) => selectedRows[`${messageId}-${i}`],
    );

    const newSelection = { ...selectedRows };
    Array.from({ length: totalRows }).forEach((_, i) => {
      newSelection[`${messageId}-${i}`] = !allSelected;
    });
    setSelectedRows(newSelection);
  };
  const handleApproveSelected = async (
    messageId,
    selectedData,
    mappingFileId,
  ) => {
    if (isApproving) return;

    setIsApproving(true);

    try {
      if (!mappingFileId) {
        toast.error("Mapping file ID not found in the response");
        return;
      }

      const updateBulkMapperDetailsRequests = selectedData.map((row) => ({
        // Try snake_case first, then PascalCase as fallback
        targetTableName:
          row.sap_table_name || row.SAPTableName || row["SAP Table Name"] || "",
        targetFieldName:
          row.sap_column_name ||
          row.SAPColumnName ||
          row["SAP Column Name"] ||
          "",
        sourceTableName:
          row.ax_table_name || row.AXTableName || row["AX Table Name"] || "",
        sourceFieldName:
          row.ax_field_name || row.AXColumnName || row["AX Column Name"] || "",
      }));

      console.log("Payload being sent:", updateBulkMapperDetailsRequests); // Add this for debugging

      const payload = {
        updateBulkMapperDetailsRequests: updateBulkMapperDetailsRequests,
        userMappingFileId: mappingFileId,
      };

      await axiosClient.post("/api/Mapper/UpdateBulkMapperFile", payload);

      toast.success(`✅ Selected mappings approved successfully`);
      setSelectedRows({});
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error(`❌ Failed to approve mappings: ${error.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  // Opens the reject modal with the selected rows pre-filled
  const handleRejectSelected = (messageId, selectedData, mappingFileId) => {
    setRejectModal({
      open: true,
      messageId,
      selectedData,
      mappingFileId,
      reason: "missing_reference",
      severity: "Low",
      comment: "",
    });
  };

  // Submits the rejection to the thumbs-down API
  const handleRejectConfirm = async () => {
    if (isRejecting) return;
    setIsRejecting(true);

    try {
      const { messageId, selectedData, reason, severity, comment } =
        rejectModal;

      const payload = {
        message_id: messageId,
        thread_id: activeChat,
        user_id: currentUserId,
        feedback_type: "negative",
        rating: 1,
        response_type: "",
        comment: comment || "",
        metadata: {
          rejected_mappings: selectedData.map((row) => ({
            sap_table: row.sap_table_name || row.SAPTableName || "",
            sap_column: row.sap_column_name || row.SAPColumnName || "",
            ax_table: row.ax_table_name || row.AXTableName || "",
            ax_field: row.ax_field_name || row.AXColumnName || "",
          })),
        },
        reason: reason,
        severity: severity,
      };

      const response = await fetch(
        `${BASE_URL}/feedback/thumbs-down?code=${API_CODE}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      toast.success(
        `✅ ${selectedData.length} mapping(s) rejected successfully`,
      );
      setSelectedRows({});
      setRejectModal((p) => ({ ...p, open: false }));
    } catch (error) {
      console.error("Rejection Error:", error);
      toast.error(`❌ Failed to submit rejection: ${error.message}`);
    } finally {
      setIsRejecting(false);
    }
  };

  const callAgentAPI = async (query) => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/v1/map/global?code=${API_CODE}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: currentUserId,
            organization_config_id: organizationConfigId,
            thread_id: activeChat,
            query: query,
            mapping_file_id: " ",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);

        // Extract the actual error message from the response
        let errorMessage = `HTTP error! status: ${response.status}`;

        if (errorData) {
          // Try to get the error message from common error response fields
          errorMessage =
            errorData.error ||
            errorData.message ||
            errorData.detail ||
            errorData.error_msg ||
            errorData.response?.detail ||
            errorData.response?.message ||
            JSON.stringify(errorData);
        }

        return {
          success: false,
          error: errorMessage,
          errorDetails: errorData,
          hasDownloadableData: false,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        hasDownloadableData: true,
      };
    } catch (error) {
      console.error("API Error:", error);
      return {
        success: false,
        error: error.message,
        hasDownloadableData: false,
      };
    }
  };

  const generateCSVContent = (tableData) => {
    if (typeof tableData[0] === "object" && tableData[0] !== null) {
      const allKeys = [
        ...new Set(tableData.flatMap((item) => Object.keys(item))),
      ];

      return [
        allKeys.join(","),
        ...tableData.map((item) =>
          allKeys
            .map((header) => {
              const value = item[header] || "";
              return `"${String(value).replaceAll('"', '""')}"`;
            })
            .join(","),
        ),
      ].join("\n");
    }

    return [
      "Index,Value",
      ...tableData.map(
        (item, index) => `${index + 1},"${String(item).replaceAll('"', '""')}"`,
      ),
    ].join("\n");
  };

  const downloadBlob = (blob, filename) => {
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    globalThis.URL.revokeObjectURL(url);
  };

  // Helper function to find table data from common array keys
  const findTableDataFromCommonKeys = (messageData, commonArrayKeys) => {
    for (const key of commonArrayKeys) {
      if (
        messageData[key] &&
        Array.isArray(messageData[key]) &&
        messageData[key].length > 0
      ) {
        return messageData[key];
      }
    }
    return null;
  };

  // Helper function to find table data from all keys
  const findTableDataFromAllKeys = (messageData, dataKeys) => {
    for (const key of dataKeys) {
      if (Array.isArray(messageData[key]) && messageData[key].length > 0) {
        return messageData[key];
      }
    }
    return null;
  };

  // Helper function to extract table data from message data
  const extractTableData = (messageData) => {
    if (Array.isArray(messageData)) {
      return messageData;
    }

    if (messageData && typeof messageData === "object") {
      const commonArrayKeys = ["results", "sap_columns", "sap_tables", "data"];
      const tableData = findTableDataFromCommonKeys(
        messageData,
        commonArrayKeys,
      );

      if (tableData) {
        return tableData;
      }

      const dataKeys = Object.keys(messageData);
      return findTableDataFromAllKeys(messageData, dataKeys);
    }

    return null;
  };

  // Helper function to validate and process table data
  const validateAndProcessTableData = (tableData, format, filename) => {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
      throw new Error("No valid data to export - no arrays found in the data");
    }

    if (format !== "csv") {
      throw new Error("Only CSV format is supported in fallback mode");
    }

    const csvContent = generateCSVContent(tableData);
    const blob = new Blob([csvContent], { type: "text/csv" });
    downloadBlob(blob, filename);
  };

  const generateFileFromDisplayedData = (messageData, format, filename) => {
    try {
      const tableData = extractTableData(messageData);
      validateAndProcessTableData(tableData, format, filename);
      return { success: true };
    } catch (error) {
      console.error("generateFileFromDisplayedData error:", error);
      return { success: false, error: error.message };
    }
  };

  const handleCSVDownload = async (response, filename) => {
    const contentType = response.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      const jsonData = await response.json();
      let tableData = null;

      if (Array.isArray(jsonData)) {
        tableData = jsonData;
      } else if (jsonData.results && Array.isArray(jsonData.results)) {
        tableData = jsonData.results;
      } else if (jsonData.sap_columns && Array.isArray(jsonData.sap_columns)) {
        tableData = jsonData.sap_columns;
      } else {
        const arrayKeys = Object.keys(jsonData).filter(
          (key) => Array.isArray(jsonData[key]) && jsonData[key].length > 0,
        );
        if (arrayKeys.length > 0) {
          tableData = jsonData[arrayKeys[0]];
        }
      }

      if (!tableData || tableData.length === 0) {
        throw new Error(
          "No valid data found in API response for CSV conversion",
        );
      }

      const csvContent = generateCSVContent(tableData);
      const blob = new Blob([csvContent], { type: "text/csv" });
      downloadBlob(blob, filename);
    } else {
      const csvText = await response.text();
      const blob = new Blob([csvText], { type: "text/csv" });
      downloadBlob(blob, filename);
    }

    return { success: true };
  };

  const downloadResponse = async (query, format = "csv") => {
    try {
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replaceAll(":", "-");
      const filename = `mapping_result_${timestamp}`;

      const response = await fetch(
        `${BASE_URL}/download-last-result?code=${API_CODE}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: query,
            download_format: format,
            filename: filename,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Download failed! status: ${response.status}`);
      }

      if (format === "csv") {
        return await handleCSVDownload(response, filename);
      }

      return { success: false, error: "Unsupported format" };
    } catch (error) {
      console.error("Download Error Details:", error);
      return {
        success: false,
        error: error.message || "Download failed due to network error",
      };
    }
  };

  // Helper function to process string data
  const processStringData = (data) => {
    try {
      const parsedData = JSON.parse(data);
      if (Array.isArray(parsedData) && parsedData.length > 0) {
        return {
          aiResponseData: parsedData,
          aiResponseText: "Here are the results:",
          hasDownloadableData: true,
        };
      }
      if (parsedData && typeof parsedData === "object") {
        return {
          aiResponseData: parsedData,
          aiResponseText:
            parsedData.message || parsedData.detail || "Here are the results:",
          hasDownloadableData: false,
        };
      }
      return {
        aiResponseData: null,
        aiResponseText: data,
        hasDownloadableData: false,
      };
    } catch (parseError) {
      console.error("Error parsing API result data:", parseError);
      return {
        aiResponseData: null,
        aiResponseText: data,
        hasDownloadableData: false,
      };
    }
  };

  // Helper function to process object data
  const processObjectDataResponse = (data) => {
    const isSingleMessage = (dataObj, key) =>
      dataObj[key] &&
      typeof dataObj[key] === "string" &&
      Object.keys(dataObj).length === 1;

    if (isSingleMessage(data, "message")) {
      return {
        aiResponseData: null,
        aiResponseText: data.message,
        hasDownloadableData: false,
      };
    }

    if (isSingleMessage(data, "detail")) {
      return {
        aiResponseData: null,
        aiResponseText: data.detail,
        hasDownloadableData: false,
      };
    }

    const hasDownloadableData =
      (data.ax_tables && data.sap_tables) ||
      Object.keys(data).some(
        (key) => Array.isArray(data[key]) && data[key].length > 0,
      );

    return {
      aiResponseData: data,
      aiResponseText: data.message || data.detail || "Here are the results:",
      hasDownloadableData,
    };
  };

  const processAPIResponse = (apiResult) => {
    // NEW CODE: Handle answer field from API response
    if (apiResult.data?.answer) {
      const cleanedAnswer = apiResult.data.answer
        .replaceAll(/^#+\s*/gm, "") // Remove # headers
        .replaceAll("**", "") // Remove bold markdown
        .trim();
      return {
        aiResponseData: null,
        aiResponseText: cleanedAnswer,
        hasDownloadableData: false,
      };
    }

    if (typeof apiResult.data === "string") {
      return processStringData(apiResult.data);
    }

    if (Array.isArray(apiResult.data)) {
      return {
        aiResponseData: apiResult.data,
        aiResponseText: "Here are the results:",
        hasDownloadableData: true,
      };
    }

    if (apiResult.data && typeof apiResult.data === "object") {
      return processObjectDataResponse(apiResult.data);
    }

    return {
      aiResponseData: null,
      aiResponseText: apiResult.data
        ? String(apiResult.data)
        : "No results found.",
      hasDownloadableData: false,
    };
  };

  const sendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: message,
      sender: "user",
      timestamp: new Date(),
    };

    const currentQuery = message;

    setChats((prevChats) =>
      prevChats.map((chat) => {
        if (chat.id === activeChat) {
          const truncatedMessage =
            message.length > 30
              ? message.slice(0, 30) + "..."
              : message.slice(0, 30);
          return {
            ...chat,
            messages: [...chat.messages, userMessage],
            title: chat.messages.length <= 1 ? truncatedMessage : chat.title,
          };
        }
        return chat;
      }),
    );

    setMessage("");
    setIsTyping(true);

    try {
      const apiResult = await callAgentAPI(currentQuery);

      if (!apiResult.success) {
        const errorResponse = {
          id: Date.now() + 1,
          text: apiResult.error,
          sender: "ai",
          timestamp: new Date(),
          hasDownload: false,
        };

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === activeChat
              ? { ...chat, messages: [...chat.messages, errorResponse] }
              : chat,
          ),
        );
        setIsTyping(false);
        return;
      }

      if (apiResult.success) {
        const { aiResponseData, aiResponseText, hasDownloadableData } =
          processAPIResponse(apiResult);

        const aiResponse = {
          id: Date.now() + 1,
          text: aiResponseText,
          sender: "ai",
          timestamp: new Date(),
          hasDownload: hasDownloadableData,
          originalQuery: currentQuery,
          data: aiResponseData,
          mappingFileId:
            Array.isArray(apiResult.data) && apiResult.data.length > 0
              ? apiResult.data[0].mapping_file_id
              : apiResult.data?.mapping_file_id || null,
        };

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === activeChat
              ? { ...chat, messages: [...chat.messages, aiResponse] }
              : chat,
          ),
        );
      } else {
        const errorResponse = {
          id: Date.now() + 1,
          text: `I apologize, but I encountered an error while processing your request: ${apiResult.error}. Please try again or contact support if the issue persists.`,
          sender: "ai",
          timestamp: new Date(),
          hasDownload: false,
        };

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === activeChat
              ? { ...chat, messages: [...chat.messages, errorResponse] }
              : chat,
          ),
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorResponse = {
        id: Date.now() + 1,
        text: `I apologize, but I'm currently unable to process your request due to a technical issue. Please try again later.`,
        sender: "ai",
        timestamp: new Date(),
        hasDownload: false,
      };

      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === activeChat
            ? { ...chat, messages: [...chat.messages, errorResponse] }
            : chat,
        ),
      );
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleDownload = async (message, format) => {
    if (!message.originalQuery) return;

    const result = await downloadResponse(message.originalQuery, format);
    if (!result.success) {
      if (message.data) {
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replaceAll(":", "-");
        const filename = `mapping_result_${timestamp}`;

        const fallbackResult = generateFileFromDisplayedData(
          message.data,
          format,
          filename,
        );
        if (!fallbackResult.success) {
          toast.error(
            `Download failed: ${result.error}. Fallback also failed: ${fallbackResult.error}`,
          );
        }
      } else {
        toast.error(`Download failed: ${result.error}`);
      }
    }
  };

  const handleFeedbackSubmit = async (messageId, feedbackData) => {
    try {
      const endpoint =
        feedbackData.type === "positive"
          ? `${BASE_URL}/feedback/thumbs-up?code=${API_CODE}`
          : `${BASE_URL}/feedback/thumbs-down?code=${API_CODE}`;

      const basePayload = {
        message_id: messageId,
        thread_id: activeChat,
        user_id: currentUserId,
        feedback_type:
          feedbackData.type === "positive" ? "positive" : "negative",
        rating:
          feedbackData.rating || (feedbackData.type === "positive" ? 5 : 1),
        response_type: "",
        comment: feedbackData.comment || "",
        metadata: {
          additionalProp1: {},
        },
      };

      const payload =
        feedbackData.type === "positive"
          ? { ...basePayload, reason: "", severity: "" }
          : {
              ...basePayload,
              reason: feedbackData.category || "string",
              severity: feedbackData.severity || "string",
            };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success("Thank you for your feedback!");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    }
  };

  const regenerateResponse = async (query) => {
    try {
      const response = await fetch(
        `${BASE_URL}/response/regenerate-agentic?code=${API_CODE}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: query,
            thread_id: activeChat,
            user_id: currentUserId,
            // corporation_id: selectedCorporation.value,
            organization_config_id: organizationConfigId,
            mapping_file_id: " ",
            download_format: " ",
            filename: " ",
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        hasDownloadableData: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        hasDownloadableData: false,
      };
    }
  };

  const handleRegenerate = async (messageId) => {
    if (regeneratingMessageId) return;

    const currentChatData = chats.find((chat) => chat.id === activeChat);
    if (!currentChatData) return;

    const messageIndex = currentChatData.messages.findIndex(
      (msg) => msg.id === messageId,
    );

    if (messageIndex === -1 || messageIndex === 0) return;

    const aiMessage = currentChatData.messages[messageIndex];

    if (aiMessage.sender !== "ai" || !aiMessage.originalQuery) {
      toast.error("Cannot regenerate this message");
      return;
    }

    setRegeneratingMessageId(messageId);

    try {
      const apiResult = await regenerateResponse(aiMessage.originalQuery);

      if (apiResult.success) {
        const { aiResponseData, aiResponseText, hasDownloadableData } =
          processAPIResponse(apiResult);

        const regeneratedMessage = {
          id: messageId,
          text: aiResponseText,
          sender: "ai",
          timestamp: new Date(),
          hasDownload: hasDownloadableData,
          originalQuery: aiMessage.originalQuery,
          data: aiResponseData,
          isRegenerated: true,
          mappingFileId: apiResult.data?.mapping_file_id || null,
        };

        // Helper function to update regenerated message in chat
        const updateRegeneratedMessage = (chat) => {
          if (chat.id === activeChat) {
            return {
              ...chat,
              messages: chat.messages.map((msg) =>
                msg.id === messageId ? regeneratedMessage : msg,
              ),
            };
          }
          return chat;
        };

        setChats((prevChats) => prevChats.map(updateRegeneratedMessage));

        toast.success("Response regenerated successfully!");
      } else {
        toast.error(`Failed to regenerate: ${apiResult.error}`);
      }
    } catch (error) {
      console.error("Error regenerating response:", error);
      toast.error("An error occurred while regenerating the response");
    } finally {
      setRegeneratingMessageId(null);
    }
  };

  const currentChat = chats.find((chat) => chat.id === activeChat);

  return (
    <>
      <style>{`
      .table-scroll-container::-webkit-scrollbar {
        height: 8px;
      }
      .table-scroll-container::-webkit-scrollbar-track {
        background: #f1f5f9;
        border-radius: 4px;
      }
      .table-scroll-container::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
      }
      .table-scroll-container::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }
    `}</style>

      <RejectConfirmModal
        rejectModal={rejectModal}
        setRejectModal={setRejectModal}
        onConfirm={handleRejectConfirm}
        isRejecting={isRejecting}
      />

      {/* ✅ Modal is OUTSIDE the disabled div — always interactive */}
      <HierarchyPreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewData({});
          setPreviewTableName([]);
          setPreviewMappingRows([]);
        }}
        previewData={previewData}
        previewLoading={previewLoading}
        mappingRows={previewMappingRows}
      />

      {/* ✅ Main layout — disabled when preview open */}
      <div
        className={`flex bg-gray-50 overflow-x-hidden ${previewOpen ? "pointer-events-none select-none opacity-50" : ""}`}
        style={{ height: "calc(100vh - 140px)" }}
      >
        <ChatSidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          chats={chats}
          activeChat={activeChat}
          setActiveChat={setActiveChat}
          createNewChat={createNewChat}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          renamingChatId={renamingChatId}
          setRenamingChatId={setRenamingChatId}
          renameValue={renameValue}
          setRenameValue={setRenameValue}
          handleRenameSubmit={handleRenameSubmit}
          handleRenameCancel={handleRenameCancel}
          handleDeleteClick={handleDeleteClick}
          toggleMenu={toggleMenu}
          handleRenameClick={handleRenameClick}
        />

        <DeleteConfirmModal
          deleteConfirmId={deleteConfirmId}
          chats={chats}
          handleDeleteConfirm={handleDeleteConfirm}
          handleDeleteCancel={handleDeleteCancel}
        />

        <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
          <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between flex-shrink-0 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-gray-600 hover:text-gray-800 flex items-center justify-center w-8 h-8"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-800 leading-8">
                {currentChat?.title || "Falcon AI Chat"}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <img
                src={logo}
                alt="Falcon AI Logo"
                className="w-8 h-8 rounded-full object-cover cursor-pointer"
              />
            </div>
          </div>

          <ChatMessages
            currentChat={currentChat || { messages: [] }}
            loadingMessages={loadingMessages}
            isTyping={isTyping}
            selectedRows={selectedRows}
            handleRowSelect={handleRowSelect}
            handleSelectAll={handleSelectAll}
            isApproving={isApproving}
            regeneratingMessageId={regeneratingMessageId}
            handleFeedbackSubmit={handleFeedbackSubmit}
            handleRegenerate={handleRegenerate}
            handleDownload={handleDownload}
            handleApproveSelected={handleApproveSelected}
            handleRejectSelected={handleRejectSelected}
            isRejecting={isRejecting}
            messagesEndRef={messagesEndRef}
            handlePreview={handlePreview}
          />

          <ChatInput
            message={message}
            setMessage={setMessage}
            sendMessage={sendMessage}
            isTyping={isTyping}
            textareaRef={textareaRef}
            handleKeyPress={handleKeyPress}
          />
        </div>
      </div>
    </>
  );
};

export default FalconAIChatbot;
